import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminLayout from "../../layouts/AdminLayout";
import { Table, Segmented, DatePicker } from "antd";
import API from "../../utils/api";
import Loader from "../../components/Loader";
import dayjs from "dayjs";

const Scoreboard = () => {
  const [range, setRange] = useState("daily");
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.set("range", range);
      if (date) params.set("date", date.format("YYYY-MM-DD"));

      const res = await API.get(`/api/scoreboard?${params.toString()}`);

      setRows(res?.data?.data || []);
      setTotals(res?.data?.totals || null);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [range, date]);

  const columns = useMemo(
    () => [
      {
        title: "TL / Manager",
        dataIndex: "manager",
        key: "manager",
        width: 180,
      },
      {
        title: "Total",
        dataIndex: "totalOrders",
        key: "totalOrders",
        width: 90,
      },
      { title: "SHIPPED", dataIndex: "SHIPPED", key: "SHIPPED", width: 90 },
      { title: "PNA", dataIndex: "PNA", key: "PNA", width: 80 },
      { title: "HMI", dataIndex: "HMI", key: "HMI", width: 80 },
      { title: "RA", dataIndex: "RA", key: "RA", width: 80 },
      { title: "RTD", dataIndex: "RTD", key: "RTD", width: 80 },
      { title: "Archived", dataIndex: "Archived", key: "Archived", width: 100 },
      { title: "LNF", dataIndex: "LNF", key: "LNF", width: 80 },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div>
        {/* Header motion */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 w-full py-4 px-4 bg-gradient-to-r from-blue-500 to-red-300 shadow-lg rounded-lg"
        >
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-white">Scoreboard</h1>
            <p className="text-xs text-white/80">
              {date ? `Date: ${date.format("DD MMM YYYY")}` : ""}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* ✅ Date filter */}
            <DatePicker
              value={date}
              onChange={(v) => setDate(v)}
              allowClear
              format="DD MMM YYYY"
              placeholder="Pick date"
              disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            />

            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <Segmented
                value={range}
                onChange={setRange}
                options={[
                  { label: "Today", value: "daily" },
                  { label: "This Week", value: "weekly" },
                  { label: "This Month", value: "monthly" },
                ]}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Loader ↔ Table animation swap */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Loader />
            </motion.div>
          ) : (
            <motion.div
              key={`table-${range}-${date ? date.format("YYYY-MM-DD") : "no-date"}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="rounded-xl overflow-hidden"
            >
              <Table
                rowKey={(r) => r.manager}
                dataSource={rows}
                columns={columns}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 800 }}
                onRow={(record) => ({
                  onClick: () => {
                    const base = `/admin/scoreboard/orders/${encodeURIComponent(
                      record.manager,
                    )}`;

                    const params = new URLSearchParams();
                    params.set("range", range);
                    if (date) params.set("date", date.format("YYYY-MM-DD"));

                    window.open(`${base}?${params.toString()}`, "_blank");
                  },
                })}
                rowClassName={() =>
                  "cursor-pointer transition-all hover:bg-gray-200 hover:translate-y-[1px] hover:shadow-[10px_2px_10px_rgba(0,0,0,0.06)]"
                }
                summary={() => {
                  if (!totals) return null;

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row className="bg-gray-50 font-bold">
                        <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          {totals.totalOrders}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          {totals.SHIPPED}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          {totals.PNA}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          {totals.HMI}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          {totals.RA}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          {totals.RTD}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          {totals.Archived}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>
                          {totals.LNF}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default Scoreboard;
